import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, Fade } from "@material-ui/core";
import { map } from "ramda";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import { useOrganizationDetails } from "../organizationServices";
import { notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { fetchSecurityUser } from "../../util/fetchUtils";
import { mergeRight } from "ramda";
import { useSelector } from "react-redux";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import YesNoCard from "../../components/YesNoCard";
import { makeStyles } from "@material-ui/core/styles";
import ReadOnlyField from "../../components/ReadOnlyField";
import EditOrganizationDetailsDialog from "../edit/EditOrganizationDetailsDialog";

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationDetailsCard = ({ id }) => {
  const auth = useSelector(state => state.auth);
  const classes = useStyles();
  const [organization, setOrganization] = useState(null);

  const [saveAlert, setSaveAlert] = useState(false);
  const timerAutoHide = React.useRef();
  const setAutoHideTimer = useCallback(timeoutDuration => {
    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(() => {
      setSaveAlert(false);
    }, timeoutDuration);
  }, []);

  React.useEffect(() => {
    if (saveAlert) {
      setAutoHideTimer(3000);
    }

    return () => {
      clearTimeout(timerAutoHide.current);
    };
  }, [saveAlert, setAutoHideTimer]);

  const detailsResponse = useOrganizationDetails(id);
  useEffect(() => {
    if (detailsResponse.isSuccess && notBlank(detailsResponse.data)) {
      const fetchUsers = async org => {
        const createdByUser = await lookupUser(auth, org.createdBy);
        const updatedByUser = await lookupUser(auth, org.updatedBy);

        return mergeRight(org, {
          createdByUsername: createdByUser.username,
          updatedByUsername: updatedByUser.username,
        });
      };

      fetchUsers(detailsResponse.data).then(setOrganization);
    }
  }, [detailsResponse.isSuccess, detailsResponse.data, auth]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditSave = useCallback(
    async data => {
      await detailsResponse.refetch();
      setEditDialogOpen(false);
      setSaveAlert(true);
    },
    [detailsResponse],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Details"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Fade in={saveAlert}>
              <Box pr={5} color="green">
                Update Successful
              </Box>
            </Fade>
            <Tooltip title="Edit Details">
              <IconButton onClick={handleEditClick}>
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {detailsResponse.isLoading && <PanelLoadingIndicator />}
        {detailsResponse.isError && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Organization Details.
          </Box>
        )}
        {detailsResponse.isSuccess && notBlank(organization) && (
          <Grid container spacing={3}>
            <Grid item sm={5}>
              <Grid container spacing={3}>
                <Grid item sm={8}>
                  <LabeledReadOnly label="Name">
                    <ReadOnlyField>
                      {organization.organizationName}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="User Creation Allowed">
                    <YesNoCard value={organization.userCreationAllowed} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={2}>
                  <LabeledReadOnly label="Active">
                    <YesNoCard value={organization.active} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Alias">
                    <ReadOnlyField>{organization.alias}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="Priority">
                    <YesNoCard value={organization.priority} />
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Buying Groups">
                {notBlank(organization.buyingGroups) ? (
                  map(
                    bg => <Typography key={bg.organizationId}>{bg.organizationName}</Typography>,
                    organization.buyingGroups,
                  )
                ) : (
                  <Typography>--</Typography>
                )}
              </LabeledReadOnly>
            </Grid>

            {/*Auditing Fields*/}
            <Grid item sm={4}>
              <Grid container spacing={3}>
                <Grid item sm={6}>
                  <LabeledReadOnly label="Created By">
                    <ReadOnlyField>
                      {organization.createdByUsername}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Date Created">
                    <ReadOnlyField>
                      {new Date(organization.dateCreated).toLocaleDateString()}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Updated By">
                    <ReadOnlyField>
                      {organization.updatedByUsername}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Date Updated">
                    <ReadOnlyField>
                      {new Date(organization.dateUpdated).toLocaleDateString()}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <EditOrganizationDetailsDialog
          open={editDialogOpen}
          details={organization}
          onCancel={handleEditCancel}
          onSave={handleEditSave}
        />
      )}
    </Card>
  );
};

export default OrganizationDetailsCard;
